import { Alert, Button, Space, Typography, Popconfirm } from 'antd';
import { InfoCircleOutlined, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface ClosureStatusBannerProps {
  participationStatus: 'active' | 'pending_close' | 'exited';
  exitEffectiveAt?: string;
  onCancelClosure?: () => void;
  canCancelClosure?: boolean;
}

export function ClosureStatusBanner({
  participationStatus,
  exitEffectiveAt,
  onCancelClosure,
  canCancelClosure = false,
}: ClosureStatusBannerProps) {
  const { t } = useTranslation('dashboard');

  if (participationStatus === 'active') {
    return null;
  }

  if (participationStatus === 'pending_close') {
    return (
      <Alert
        message={
          <Space>
            <ClockCircleOutlined />
            <strong>{t('closureStatus.pendingTitle', 'Account Closure Pending')}</strong>
          </Space>
        }
        description={
          <Space orientation="vertical" size="small">
            <Text>
              {t(
                'closureStatus.pendingMessage',
                'Your account closure request is being processed. You will receive one more profit allocation in the next weekly settlement, after which your account will be closed and a final withdrawal will be created.'
              )}
            </Text>
            {canCancelClosure && onCancelClosure && (
              <Popconfirm
                title={t('closureStatus.cancelConfirmTitle', 'Cancel closure request?')}
                description={t('closureStatus.cancelConfirmDescription', 'Are you sure you want to cancel your account closure request? Your account will remain active and you will continue to participate in the pool.')}
                onConfirm={onCancelClosure}
                okText={t('closureStatus.cancelConfirmYes', 'Yes, cancel request')}
                cancelText={t('closureStatus.cancelConfirmNo', 'No, keep request')}
              >
                <Button size="small">
                  {t('closureStatus.cancelButton', 'Cancel Closure Request')}
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
        type="warning"
        showIcon
        icon={<ClockCircleOutlined />}
        style={{ marginBottom: 24 }}
      />
    );
  }

  if (participationStatus === 'exited') {
    return (
      <Alert
        message={
          <Space>
            <CheckCircleOutlined />
            <strong>{t('closureStatus.exitedTitle', 'Account Closed')}</strong>
          </Space>
        }
        description={
          <Space orientation="vertical" size="small">
            <Text>
              {t(
                'closureStatus.exitedMessage',
                'Your account has been closed and you are no longer participating in the pool. No new profits will be allocated to you.'
              )}
            </Text>
            {exitEffectiveAt && (
              <Text type="secondary">
                {t('closureStatus.exitedAt', 'Effective date')}: {new Date(exitEffectiveAt).toLocaleDateString()}
              </Text>
            )}
            <Text>
              {t(
                'closureStatus.historyAvailable',
                'You can still view your historical contributions, settlements, and withdrawals below.'
              )}
            </Text>
          </Space>
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 24 }}
      />
    );
  }

  return null;
}
